import React, { useEffect, useState, useContext, useCallback } from "react";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import SvgIcon from "@material-ui/core/SvgIcon";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";

const CloseXIcon = (props) => (
	<SvgIcon {...props} viewBox="0 0 24 24">
		<path d="M18 6L6.00081 17.9992M17.9992 18L6 6.00085" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
	</SvgIcon>
);

import formatSerializedId from '../../utils/formatSerializedId';
import { i18n } from "../../translate/i18n";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import CustomToggleSwitch from "../CustomToggleSwitch";
import { ContactForm } from "../ContactForm";
import ContactModal from "../ContactModal";
import { ContactNotes } from "../ContactNotes";
import MediaDrawer from "../MediaDrawer";

import { CountryFlag } from "../../utils/CountryFlag";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { toast } from "react-toastify";


const useStyles = makeStyles(theme => ({
	// Drawer Paper — mesmo visual do dialogPaper do design system.
	// Bordas arredondadas no lado oposto à âncora (anchor=right => esquerda).
	drawerPaper: {
		...theme.chatiaDS.dialogPaper,
		borderRadius: "21px 0 0 12px !important",
		maxHeight: "none !important",
		height: "100%",
		backgroundColor: theme.mode === "light" ? "#ffffff !important" : "#121212 !important",
		backgroundImage: "none !important",
		border: "none",
	},

	content: {
		display: "flex",
		flexDirection: "column",
		height: "100%",
		overflowY: "auto",
		...theme.scrollbarStyles,
	},

	// Header (igual ao dialog)
	header: {
		...theme.chatiaDS.header,
	},
	headerTitle: {
		...theme.chatiaDS.headerTitle,
	},
	closeButton: {
		backgroundColor: theme.chatiaDS.closeButton.backgroundColor,
		color: theme.chatiaDS.closeButton.color,
		transition: theme.chatiaDS.closeButton.transition,
	},

	// Profile section
	profileSection: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		padding: "24px 16px 16px",
	},
	avatar: {
		width: 120,
		height: 120,
		borderRadius: "50%",
		border: theme.mode === "light"
			? `3px solid ${theme.chatiaDS.colors.border}`
			: `3px solid ${theme.chatiaDS.colors.border}`,
		marginBottom: 12,
		cursor: "pointer",
		objectFit: "cover",
	},
	contactName: {
		fontSize: 18,
		fontWeight: 600,
		color: theme.chatiaDS.colors.textPrimary,
		textAlign: "center",
		cursor: "pointer",
		display: "flex",
		alignItems: "center",
		gap: 6,
		"&:hover": {
			color: theme.palette.primary.main,
		},
	},
	contactNumber: {
		fontSize: 13,
		color: theme.chatiaDS.colors.textSecondary,
		marginTop: 2,
	},
	contactEmail: {
		fontSize: 13,
		color: theme.palette.primary.main,
		marginTop: 2,
	},

	// Action buttons row
	actionsRow: {
		display: "flex",
		justifyContent: "center",
		gap: 12,
		padding: "8px 16px 16px",
	},
	actionBtn: {
		...theme.chatiaDS.actionBtn,
	},

	// Divider
	divider: {
		...theme.chatiaDS.divider,
	},

	// Tabs
	tabs: {
		...theme.chatiaDS.tabs,
	},
	tabContent: {
		flex: 1,
		overflowY: "auto",
		...theme.scrollbarStyles,
	},

	// Section card
	section: {
		...theme.chatiaDS.section,
	},
	sectionTitle: {
		...theme.chatiaDS.sectionTitle,
	},

	// Toggle row
	toggleItem: {
		...theme.chatiaDS.toggleItem,
	},
	toggleLabel: {
		...theme.chatiaDS.toggleLabel,
	},

	// Extra info — usa fieldRow/fieldLabel/fieldValue do DS
	extraInfoItem: {
		padding: "8px 0",
		borderBottom: theme.chatiaDS.fieldRow.borderBottom,
		"&:last-child": {
			borderBottom: "none",
		},
	},
	extraInfoLabel: {
		...theme.chatiaDS.fieldLabel,
		marginBottom: 2,
	},
	extraInfoValue: {
		...theme.chatiaDS.fieldValue,
	},

	// Qualification section — variante "accent" do DS
	qualificationSection: {
		...theme.chatiaDS.sectionAccent,
	},
	qualFieldRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 12,
		padding: "8px 0",
		borderBottom: theme.mode === "light" ? "1px solid #e3edf7" : "1px solid #253545",
		"&:last-child": { borderBottom: "none" },
	},
	qualFieldLabel: {
		...theme.chatiaDS.fieldLabel,
		color: theme.mode === "light" ? "#5a7fa0" : theme.chatiaDS.colors.textSecondary,
		minWidth: 70,
		flex: "0 0 auto",
	},
	qualFieldValue: {
		flex: 1,
		textAlign: "right",
	},
	qualTextareaDisplay: {
		fontSize: 12,
		cursor: "pointer",
		padding: "4px 6px",
		borderRadius: theme.chatiaDS.radius.xs,
		whiteSpace: "pre-wrap",
		lineHeight: 1.5,
		maxHeight: 120,
		overflow: "auto",
		...theme.scrollbarStylesSoft,
	},
	qualFieldInput: {
		"& .MuiInputBase-input": {
			fontSize: 13,
			padding: "4px 8px",
			textAlign: "right",
		},
		"& .MuiOutlinedInput-root": {
			borderRadius: theme.chatiaDS.radius.sm,
		},
	},
	qualBadge: {
		...theme.chatiaDS.badge,
		cursor: "pointer",
		transition: "all 0.2s ease",
	},
	qualEmpty: {
		...theme.chatiaDS.empty,
		padding: "12px 0",
		fontSize: 12,
	},

	// Notes section
	notesSection: {
		...theme.chatiaDS.section,
		"& .MuiTextField-root": {
			width: "100%",
			margin: 0,
			"& .MuiOutlinedInput-root": {
				borderRadius: theme.chatiaDS.radius.sm,
				fontSize: 14,
				backgroundColor: theme.mode === "light" ? "#fff" : "#111b21",
			},
			"& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
				backgroundColor: theme.mode === "light" ? "#fff" : "#111b21",
				padding: "0 6px",
				marginLeft: -3,
			},
		},
		"& .MuiList-root": {
			maxWidth: "100%",
			width: "100%",
		},
		"& .MuiGrid-spacing-xs-2": {
			margin: 0,
			width: "100%",
			"& > .MuiGrid-item": {
				padding: "4px 0",
			},
		},
		"& .MuiButton-root": {
			borderRadius: theme.chatiaDS.radius.sm,
			textTransform: "none",
			fontSize: 13,
		},
	},

}));

// Cores para badges de qualificacao tipo select
const SELECT_COLORS = {
	"quente": { bg: "#dcfce7", color: "#166534", border: "#86efac" },
	"morno": { bg: "#fef9c3", color: "#854d0e", border: "#fde047" },
	"curioso": { bg: "#dbeafe", color: "#1e40af", border: "#93c5fd" },
	"frio": { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1" },
};

const getSelectColor = (value) => {
	if (!value) return { bg: "#f1f5f9", color: "#94a3b8", border: "#e2e8f0" };
	const lower = value.toLowerCase().trim();
	return SELECT_COLORS[lower] || { bg: "#ede9fe", color: "#6d28d9", border: "#c4b5fd" };
};

const ContactDrawer = ({ open, handleDrawerClose, contact, ticket, loading, embedded }) => {
	const classes = useStyles();
	const theme = useTheme();

	const [modalOpen, setModalOpen] = useState(false);
	const [blockingContact, setBlockingContact] = useState(contact.active);
	const [openForm, setOpenForm] = useState(false);
	const { get } = useCompanySettings();
	const [hideNum, setHideNum] = useState(false);
	const { user } = useContext(AuthContext);
	const [acceptAudioMessage, setAcceptAudio] = useState(contact.acceptAudioMessage);
	// botActive = bot está ativo (em fluxo e não desabilitado)
	const [botActive, setBotActive] = useState(!!ticket?.lastFlowId && !contact.disableBot);
	const [activeTab, setActiveTab] = useState(0);
	const [mediaDrawerOpen, setMediaDrawerOpen] = useState(false);

	// Qualificacao
	const [qualSchema, setQualSchema] = useState(null);
	const [qualValues, setQualValues] = useState({});
	const [qualLoading, setQualLoading] = useState(false);
	const [editingField, setEditingField] = useState(null);
	const [editValue, setEditValue] = useState("");


	const loadQualification = useCallback(async () => {
		if (!contact?.id) return;
		try {
			setQualLoading(true);
			const { data } = await api.get(`/contacts/${contact.id}/qualification`);
			if (data.datasetId) {
				setQualSchema(data);
				const vals = {};
				(data.fields || []).forEach(f => { vals[f.id] = f.value || ""; });
				setQualValues(vals);
			} else {
				setQualSchema(null);
			}
		} catch (err) {
			setQualSchema(null);
		}
		setQualLoading(false);
	}, [contact?.id]);

	useEffect(() => {
		loadQualification();
	}, [loadQualification]);

	// Sincronizar estado do bot quando ticket ou contato muda
	useEffect(() => {
		setBotActive(!!ticket?.lastFlowId && !contact?.disableBot);
	}, [ticket?.lastFlowId, contact?.disableBot]);

	useEffect(() => {
		if (!qualSchema || !contact?.extraInfo) return;
		const vals = { ...qualValues };
		let changed = false;
		for (const field of qualSchema.fields || []) {
			const match = contact.extraInfo.find(e => e.name === field.id);
			if (match && match.value !== vals[field.id]) {
				vals[field.id] = match.value;
				changed = true;
			}
		}
		if (changed) setQualValues(vals);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contact?.extraInfo, qualSchema]);

	const saveQualField = async (fieldId, value) => {
		try {
			await api.put(`/contacts/${contact.id}/qualification`, {
				fields: { [fieldId]: value }
			});
			setQualValues(prev => ({ ...prev, [fieldId]: value }));
		} catch (err) {
			toastError(err);
		}
		setEditingField(null);
	};

	useEffect(() => {
		async function fetchData() {
			const lgpdHideNumber = await get({ "column": "lgpdHideNumber" });
			if (lgpdHideNumber === "enabled") setHideNum(true);
		}
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setOpenForm(false);
	}, [open, contact]);

	const handleContactToggleAcceptAudio = async () => {
		try {
			const resp = await api.put(`/contacts/toggleAcceptAudio/${ticket.contact.id}`);
			setAcceptAudio(resp.data.acceptAudioMessage);
		} catch (err) {
			toastError(err);
		}
	};

	const handleContactToggleDisableBot = async () => {
		try {
			const { data } = await api.put(`/contacts/toggleDisableBot/${ticket.contact.id}`);
			if (data.disableBot && ticket) {
				ticket.lastFlowId = null;
				ticket.flowWebhook = false;
			}
			setBotActive(!!ticket?.lastFlowId && !data.disableBot);
		} catch (err) {
			toastError(err);
		}
	};

	const handleBlockContact = async (contactId) => {
		try {
			await api.put(`/contacts/block/${contactId}`, { active: false });
			toast.success(i18n.t("contactDrawer.toasts.contactBlocked"));
		} catch (err) {
			toastError(err);
		}
		setBlockingContact(true);
	};

	const handleUnBlockContact = async (contactId) => {
		try {
			await api.put(`/contacts/block/${contactId}`, { active: true });
			toast.success(i18n.t("contactDrawer.toasts.contactUnblocked"));
		} catch (err) {
			toastError(err);
		}
		setBlockingContact(false);
	};

	if (loading) return null;

	const formattedNumber = formatSerializedId(contact.number);
	const displayNumber = formattedNumber.slice(0, -4) + "****";


	// === TAB: Dados ===
	const renderDadosTab = () => (
		<div>
			{/* Profile Section */}
			<div className={classes.profileSection}>
				<Avatar
					className={classes.avatar}
					alt={contact.name}
					src={contact.urlPicture || ""}
				/>
				<Typography className={classes.contactName}>
					{contact.name}
				</Typography>
				<Typography className={classes.contactNumber} style={{ display: "flex", alignItems: "center", gap: 6 }}>
					<CountryFlag number={contact.number} size={18} />
					{displayNumber}
				</Typography>
				{contact.email && (
					<Link href={`mailto:${contact.email}`} className={classes.contactEmail}>
						{contact.email}
					</Link>
				)}
			</div>

			{/* Action Buttons */}
			<div className={classes.actionsRow}>
				<Button
					className={classes.actionBtn}
					variant="contained"
					color="primary"
					size="small"
					onClick={() => setModalOpen(true)}
				>
					{i18n.t("contactDrawer.buttons.edit")}
				</Button>
				<Button
					className={classes.actionBtn}
					variant="outlined"
					color="secondary"
					size="small"
					onClick={() => contact.active
						? handleBlockContact(contact.id)
						: handleUnBlockContact(contact.id)}
					disabled={loading}
				>
					{!contact.active ? i18n.t("contactDrawer.buttons.unblockContact") : i18n.t("contactDrawer.buttons.blockContact")}
				</Button>
			</div>

			{/* Inline edit form */}
			{(contact.id && openForm) && (
				<div style={{ padding: "0 12px 8px" }}>
					<ContactForm initialContact={contact} onCancel={() => setOpenForm(false)} />
				</div>
			)}

			<div className={classes.divider} />

			{/* Midia, links e docs */}
			<div
				className={classes.section}
				style={{ cursor: "pointer" }}
				onClick={() => setMediaDrawerOpen(true)}
			>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
					<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" color="currentColor" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.6 }}>
							<path d="M6 17.9745C6.1287 19.2829 6.41956 20.1636 7.07691 20.8209C8.25596 22 10.1536 22 13.9489 22C17.7442 22 19.6419 22 20.8209 20.8209C22 19.6419 22 17.7442 22 13.9489C22 10.1536 22 8.25596 20.8209 7.07691C20.1636 6.41956 19.2829 6.1287 17.9745 6" />
							<path d="M2 10C2 6.22876 2 4.34315 3.17157 3.17157C4.34315 2 6.22876 2 10 2C13.7712 2 15.6569 2 16.8284 3.17157C18 4.34315 18 6.22876 18 10C18 13.7712 18 15.6569 16.8284 16.8284C15.6569 18 13.7712 18 10 18C6.22876 18 4.34315 18 3.17157 16.8284C2 15.6569 2 13.7712 2 10Z" />
							<path d="M2 11.1185C2.61902 11.0398 3.24484 11.001 3.87171 11.0023C6.52365 10.9533 9.11064 11.6763 11.1711 13.0424C13.082 14.3094 14.4247 16.053 15 18" />
							<path d="M12.9998 7H13.0088" />
						</svg>
						<Typography style={{ fontSize: 14, fontWeight: 500 }}>
							Midia, links e docs
						</Typography>
					</div>
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4 }}>
						<path d="M9 6L15 12L9 18" />
					</svg>
				</div>
			</div>

			{/* Settings Section */}
			<div className={classes.section}>
				<div className={classes.toggleItem}>
					<span className={classes.toggleLabel}>
						{i18n.t("ticketOptionsMenu.acceptAudioMessage")}
					</span>
					<CustomToggleSwitch
						checked={acceptAudioMessage}
						onChange={handleContactToggleAcceptAudio}
					/>
				</div>
				<div className={classes.toggleItem}>
					<span className={classes.toggleLabel}>
						{botActive ? i18n.t("messagesList.header.buttons.chatbotActive") : i18n.t("messagesList.header.buttons.chatbotInactive")}
					</span>
					<CustomToggleSwitch
						checked={botActive}
						onChange={handleContactToggleDisableBot}
					/>
				</div>
			</div>

			{/* Qualificacao - agrupado por dataset (multi-agente) */}
			{qualSchema && qualSchema.fields && qualSchema.fields.length > 0 && (
				<div className={classes.qualificationSection}>
					{(qualSchema.groups && qualSchema.groups.length > 1) && qualSchema.groups.map((group, gi) => (
						<Typography key={`title-${gi}`} className={classes.sectionTitle} style={{ color: "#3b82f6", marginTop: gi > 0 ? 12 : 0, fontSize: 12 }}>
							{group.datasetName}
						</Typography>
					))}
					{(!qualSchema.groups || qualSchema.groups.length <= 1) && (
						<Typography className={classes.sectionTitle} style={{ color: "#3b82f6" }}>
							{qualSchema.datasetName || i18n.t("contactDrawer.qualification")}
						</Typography>
					)}
					{qualLoading ? (
						<div style={{ textAlign: "center", padding: 12 }}>
							<CircularProgress size={20} />
						</div>
					) : (
						qualSchema.fields.map(field => (
							<div key={field.id} className={classes.qualFieldRow}>
								<Typography className={classes.qualFieldLabel}>
									{field.label || field.name}
								</Typography>
								<div className={classes.qualFieldValue}>
									{field.type === "select" ? (
										editingField === field.id ? (
											<Select
												value={editValue}
												onChange={(e) => {
													setEditValue(e.target.value);
													saveQualField(field.id, e.target.value);
												}}
												onClose={() => setEditingField(null)}
												open
												variant="outlined"
												size="small"
												style={{ minWidth: 120, fontSize: 13 }}
											>
												<MenuItem value="">
													<em>{i18n.t("contactDrawer.none")}</em>
												</MenuItem>
												{(field.options || []).map(opt => {
													const colors = getSelectColor(opt);
													return (
														<MenuItem key={opt} value={opt}>
															<span style={{
																backgroundColor: colors.bg,
																color: colors.color,
																padding: "2px 8px",
																borderRadius: 10,
																fontSize: 12,
																fontWeight: 600,
															}}>
																{opt}
															</span>
														</MenuItem>
													);
												})}
											</Select>
										) : (
											<span
												className={classes.qualBadge}
												style={{
													backgroundColor: getSelectColor(qualValues[field.id]).bg,
													color: getSelectColor(qualValues[field.id]).color,
													border: `1px solid ${getSelectColor(qualValues[field.id]).border}`,
												}}
												onClick={() => {
													setEditingField(field.id);
													setEditValue(qualValues[field.id] || "");
												}}
											>
												{qualValues[field.id] || "\u2014"}
											</span>
										)
									) : field.type === "textarea" ? (
										editingField === field.id ? (
											<TextField
												variant="outlined"
												size="small"
												className={classes.qualFieldInput}
												value={editValue}
												onChange={(e) => setEditValue(e.target.value)}
												onBlur={() => saveQualField(field.id, editValue)}
												onKeyDown={(e) => {
													if (e.key === "Escape") setEditingField(null);
												}}
												autoFocus
												multiline
												minRows={3}
												maxRows={8}
												placeholder={`Preencher ${field.label || field.name}...`}
												fullWidth
											/>
										) : (
											<Typography
												component="div"
												className={classes.qualTextareaDisplay}
												style={{
													color: qualValues[field.id] ? undefined : "#aaa",
												}}
												onClick={() => {
													setEditingField(field.id);
													setEditValue(qualValues[field.id] || "");
												}}
											>
												{qualValues[field.id] || "Clique para preencher"}
											</Typography>
										)
									) : (
										editingField === field.id ? (
											<TextField
												variant="outlined"
												size="small"
												className={classes.qualFieldInput}
												value={editValue}
												onChange={(e) => setEditValue(e.target.value)}
												onBlur={() => saveQualField(field.id, editValue)}
												onKeyDown={(e) => {
													if (e.key === "Enter") saveQualField(field.id, editValue);
													if (e.key === "Escape") setEditingField(null);
												}}
												autoFocus
												type={field.type === "number" ? "number" : field.type === "email" ? "email" : "text"}
												placeholder={`Preencher ${field.label || field.name}...`}
												fullWidth
											/>
										) : (
											<Typography
												style={{
													fontSize: 13,
													color: qualValues[field.id] ? undefined : "#aaa",
													cursor: "pointer",
													padding: "2px 4px",
													borderRadius: 6,
													transition: "background 0.15s",
												}}
												onClick={() => {
													setEditingField(field.id);
													setEditValue(qualValues[field.id] || "");
												}}
												onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(59,130,246,0.08)"; }}
												onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
											>
												{qualValues[field.id] || "Clique para preencher"}
											</Typography>
										)
									)}
								</div>
							</div>
						))
					)}
				</div>
			)}

			{/* Extra Info */}
			{contact?.extraInfo?.length > 0 && (() => {
				const qualFieldIds = (qualSchema?.fields || []).map(f => f.id);
				const filtered = contact.extraInfo.filter(info => !qualFieldIds.includes(info.name));
				if (filtered.length === 0) return null;
				return (
					<div className={classes.section}>
						<Typography className={classes.sectionTitle}>
							{i18n.t("contactDrawer.extraInfo")}
						</Typography>
						{filtered.map(info => {
							const qualField = (qualSchema?.fields || []).find(f => f.id === info.name);
							const displayName = qualField ? (qualField.label || qualField.name) : info.name;
							return (
								<div key={info.id} className={classes.extraInfoItem}>
									<Typography className={classes.extraInfoLabel}>
										{displayName}
									</Typography>
									<Typography component="div" className={classes.extraInfoValue}>
										<MarkdownWrapper>{info.value}</MarkdownWrapper>
									</Typography>
								</div>
							);
						})}
					</div>
				);
			})()}

		</div>
	);

	// === TAB: Observacoes ===
	const renderObservacoesTab = () => (
		<div className={classes.notesSection} style={{ margin: "8px 12px", minHeight: 300 }}>
			<ContactNotes ticket={ticket} />
		</div>
	);

	// === TAB: Midias ===
	const renderContent = () => (
		<div className={classes.content}>
			{/* Tabs */}
			<Tabs
				value={activeTab}
				onChange={(e, v) => setActiveTab(v)}
				className={classes.tabs}
				indicatorColor="primary"
				textColor="primary"
				variant="fullWidth"
			>
				<Tab label={i18n.t("contactDrawer.tabs.data")} />
				<Tab label={i18n.t("contactDrawer.tabs.followUp")} />
				<Tab label={i18n.t("contactDrawer.tabs.favorites")} />
			</Tabs>

			{/* Tab Content */}
			<div className={classes.tabContent}>
				{activeTab === 0 && renderDadosTab()}
				{activeTab === 1 && renderObservacoesTab()}
				{activeTab === 2 && (
					<div style={{ textAlign: "center", padding: "60px 16px", color: "#999", fontSize: 13 }}>
						{i18n.t("contactDrawer.noFavoriteMessages")}
					</div>
				)}
			</div>

			<ContactModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				contactId={contact.id}
			/>
			<MediaDrawer
				open={mediaDrawerOpen}
				onClose={() => setMediaDrawerOpen(false)}
				ticketId={ticket?.id}
				contactName={contact?.name}
			/>
		</div>
	);

	// Modo embedded: renderiza so o conteudo, sem o Drawer
	if (embedded) {
		return renderContent();
	}

	return (
		<Drawer
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{
				className: classes.drawerPaper,
				style: { position: "absolute" },
			}}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{
				container: document.getElementById("drawer-container"),
				style: { position: "absolute" },
			}}
		>
			<div className={classes.header}>
				<Typography className={classes.headerTitle}>
					{i18n.t("contactDrawer.header")}
				</Typography>
				<IconButton
					size="small"
					onClick={handleDrawerClose}
					className={classes.closeButton}
					onMouseEnter={(e) => {
						e.currentTarget.style.backgroundColor = theme.chatiaDS.closeButtonHover.backgroundColor;
						e.currentTarget.style.color = theme.chatiaDS.closeButtonHover.color;
					}}
					onMouseLeave={(e) => {
						e.currentTarget.style.backgroundColor = theme.chatiaDS.closeButton.backgroundColor;
						e.currentTarget.style.color = theme.chatiaDS.closeButton.color;
					}}
				>
					<CloseXIcon style={{ fontSize: 18 }} />
				</IconButton>
			</div>
			{renderContent()}
		</Drawer>
	);
};

export default ContactDrawer;
